(() => {
  const STORAGE_KEY = 'ennui-storefront-shutter-seen-v16';
  const MOBILE_QUERY = '(min-width: 0px)';
  const REDUCED_QUERY = '(prefers-reduced-motion: reduce)';
  const UNIQUE_STICKER_COUNT = 40;

  // Single source of truth for the complete intro pace. The Enter Store control
  // appears after stickerBuildMs + completedHoldMs; opening takes rattleMs + liftMs.
  const INTRO_TIMING = Object.freeze({
    surfaceWaitMaxMs: 650,
    stickerBuildMs: 1120,
    placementSpreadMs: 820,
    placementBaseDurationMs: 170,
    placementDurationRangeMs: 150,
    completedHoldMs: 600,
    rattleMs: 150,
    liftMs: 1000,
  });

  class MobileHomeShutter extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.mobile-home-shutter__panel');
      this.enterButton = this.querySelector('.mobile-home-shutter__enter');
      if (!this.panel || !this.enterButton) return;

      this.mobile = window.matchMedia(MOBILE_QUERY);
      this.reduced = window.matchMedia(REDUCED_QUERY);
      this.style.setProperty('--shutter-rattle-duration', `${INTRO_TIMING.rattleMs}ms`);
      this.style.setProperty('--shutter-lift-duration', `${INTRO_TIMING.liftMs}ms`);
      this.finish = this.finish.bind(this);
      this.open = this.open.bind(this);
      this.onPanelAnimationEnd = this.onPanelAnimationEnd.bind(this);
      this.onModeChange = this.onModeChange.bind(this);
      this.enterButton.addEventListener('click', this.open);
      this.mobile.addEventListener('change', this.onModeChange);
      this.reduced.addEventListener('change', this.onModeChange);
      this.onModeChange();
    }

    disconnectedCallback() {
      clearTimeout(this.readyTimer);
      clearTimeout(this.promptTimer);
      clearTimeout(this.fallbackTimer);
      this.panel?.removeEventListener('animationend', this.onPanelAnimationEnd);
      this.enterButton?.removeEventListener('click', this.open);
      this.mobile?.removeEventListener('change', this.onModeChange);
      this.reduced?.removeEventListener('change', this.onModeChange);
      document.body.classList.remove('mobile-home-shutter-active');
      document.documentElement.classList.remove('mobile-home-shutter-boot');
    }

    hasPlayed() {
      try {
        return window.sessionStorage.getItem(STORAGE_KEY) === '1';
      } catch (error) {
        return false;
      }
    }

    remember() {
      try {
        window.sessionStorage.setItem(STORAGE_KEY, '1');
      } catch (error) {
        // Storage can be unavailable in strict privacy modes; the opener still works.
      }
    }

    onModeChange() {
      if (!this.mobile.matches || this.reduced.matches || this.hasPlayed()) {
        this.finish();
        return;
      }
      if (this.started) return;

      this.started = true;
      this.hidden = false;
      document.body.classList.add('mobile-home-shutter-active');
      this.prepareSurface().then(() => {
        if (!this.isConnected || this.hidden) return;

        this.prepareStickerTiming();
        this.setAttribute('data-shutter-active', '');
        requestAnimationFrame(() => {
          document.documentElement.classList.remove('mobile-home-shutter-boot');
        });
        this.promptTimer = setTimeout(() => {
          this.enterButton.hidden = false;
          this.setAttribute('data-shutter-ready', '');
        }, INTRO_TIMING.stickerBuildMs + INTRO_TIMING.completedHoldMs);
      });
    }

    open() {
      if (this.opening || this.hidden) return;

      this.opening = true;
      this.remember();
      this.enterButton.disabled = true;
      this.removeAttribute('data-shutter-ready');
      this.panel.addEventListener('animationend', this.onPanelAnimationEnd);
      this.setAttribute('data-shutter-opening', '');
      this.fallbackTimer = setTimeout(this.finish, INTRO_TIMING.rattleMs + INTRO_TIMING.liftMs + 250);
    }

    onPanelAnimationEnd(event) {
      if (event.target === this.panel && event.animationName === 'mobile-home-shutter-lift') {
        this.finish();
      }
    }

    prepareStickerTiming() {
      const desktop = window.matchMedia('(min-width: 750px)').matches;
      const images = [...this.querySelectorAll('.sticker-layer__item')].filter((image) =>
        desktop || !image.classList.contains('sticker-layer__item--desktop-extra')
      );

      images.forEach((image, index) => {
        const start = Math.min(1, Math.max(0, Number.parseFloat(image.dataset.stickerStart) || 0));
        const sourceDuration = Math.min(1, Math.max(0.05, Number.parseFloat(image.dataset.stickerDuration) || 0.12));
        const entryX = Math.min(12, Math.max(-12, (Number.parseFloat(image.dataset.stickerEntryX) || 0) / 12));
        const entryY = Math.min(10, Math.max(-10, (Number.parseFloat(image.dataset.stickerEntryY) || 0) / 12));
        const entryRotation = Math.min(14, Math.max(-14, (Number.parseFloat(image.dataset.stickerEntryRotation) || 0) / 2));

        // Give the first placements room to register, then tighten the gaps as
        // the surface fills. The original hand-authored placement order remains.
        const acceleratedProgress = 1 - Math.pow(1 - start, 1.7);
        const delay = 24 + acceleratedProgress * INTRO_TIMING.placementSpreadMs;
        const duration = INTRO_TIMING.placementBaseDurationMs + sourceDuration * INTRO_TIMING.placementDurationRangeMs;
        image.style.setProperty('--shutter-sticker-delay', `${delay.toFixed(0)}ms`);
        image.style.setProperty('--shutter-sticker-duration', `${duration.toFixed(0)}ms`);
        image.style.setProperty('--shutter-entry-x', `${entryX.toFixed(2)}vw`);
        image.style.setProperty('--shutter-entry-y', `${entryY.toFixed(2)}vh`);
        image.style.setProperty('--shutter-entry-rotation', `${entryRotation.toFixed(2)}deg`);
        image.dataset.weathering = this.weatheringFor(index);
      });
    }

    weatheringFor(index) {
      // Keep the first pass of unique/hero stickers crisp and readable. Repeated
      // filler placements carry more of the subtle age and surface variation.
      if (index < UNIQUE_STICKER_COUNT) return index % 9 === 0 ? 'aged' : 'clean';
      if (index % 19 === 0) return 'torn';
      if (index % 13 === 0) return 'peeled';
      if (index % 7 === 0) return 'faded';
      if (index % 5 === 0) return 'weathered';
      if (index % 3 === 0) return 'aged';
      return 'clean';
    }

    prepareSurface() {
      // Decode the single unique pass before animation. Repeated filler images
      // can resolve during the build without blocking first paint on mobile.
      const images = [...this.querySelectorAll('.sticker-layer__item')].slice(0, UNIQUE_STICKER_COUNT);
      const imageReady = Promise.all(images.map((image) => {
        if (image.complete) return Promise.resolve();
        if (image.decode) return image.decode().catch(() => undefined);

        return new Promise((resolve) => {
          image.addEventListener('load', resolve, { once: true });
          image.addEventListener('error', resolve, { once: true });
        });
      }));
      const maximumWait = new Promise((resolve) => {
        this.readyTimer = setTimeout(resolve, INTRO_TIMING.surfaceWaitMaxMs);
      });

      return Promise.race([imageReady, maximumWait]).finally(() => {
        clearTimeout(this.readyTimer);
      });
    }

    finish() {
      clearTimeout(this.readyTimer);
      clearTimeout(this.promptTimer);
      clearTimeout(this.fallbackTimer);
      this.panel?.removeEventListener('animationend', this.onPanelAnimationEnd);
      document.body.classList.remove('mobile-home-shutter-active');
      document.documentElement.classList.remove('mobile-home-shutter-boot');
      this.removeAttribute('data-shutter-active');
      this.removeAttribute('data-shutter-ready');
      this.removeAttribute('data-shutter-opening');
      if (this.enterButton) this.enterButton.hidden = true;
      this.hidden = true;
    }
  }

  if (!customElements.get('mobile-home-shutter')) {
    customElements.define('mobile-home-shutter', MobileHomeShutter);
  }
})();
